import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  MovementCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { PipeSurface3D } from 'features/assetViz/objects/surfaces/PipeSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import {
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoEditableComponent,
  GeckoMeasurementUnit,
  GeckoPipeSection,
} from 'types'

export const PipingSceneConfig: SceneFactoryConfig = {
  name: 'Piping',
  assetModels: [GeckoAssetModelType.Piping],
  componentModels: [GeckoComponentModelType.PipeSection],
  sceneFunc: (props) => <PipingScene {...props} />,
}

/**
 * A scene depicting a cylindrical pipe (not a smoking pipe, mind you) which
 * wraps around the x-axis.
 * @param assetModel      The model used to represent the whole asset
 * @param componentModel  The model for the component in question
 */
export const PipingScene: React.FC<{
  componentModel: GeckoEditableComponent
}> = ({ componentModel }) => {
  const selectedModel = componentModel as GeckoPipeSection

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    const componentBounds =
      selectedModel.componentBounds ??
      GeckoComponentRect.CreateFromXYs(
        '',
        0,
        0,
        1,
        1,
        GeckoMeasurementUnit.Meters,
        GeckoMeasurementUnit.Meters
      )
    if (
      componentBounds.x.unit !== GeckoMeasurementUnit.Meters ||
      componentBounds.y.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Pipes in the asset model should always be in meters, but 
              the x range is in ${componentBounds.x.unit}
              and y range is in ${componentBounds.y.unit}`
      )
    return componentBounds
  }, [selectedModel.componentBounds])

  const radius = React.useMemo(() => selectedModel.radius, [selectedModel])

  //----------------------------------------------------------------------------

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      componentDataFrames
        ?.at(0)
        ?.data?.geometry?.project(componentBounds.space) ?? componentBounds,
    [componentBounds, componentDataFrames]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({ rect: componentBounds, doWrapX: show3D }),
    [componentBounds, show3D]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - (componentBounds.start.x.num ?? 0)
      const relY = componentY - (componentBounds.start.y.num ?? 0)

      if (!show3D || !selectedModel) {
        return {
          position: [relX, relY, distToComponent],
          rotation: [0, 0, 0.5 * Math.PI],
        }
      }

      const angle = 2.0 * Math.PI * (relX / componentBounds.width.num - 0.25)
      const r = distToComponent + radius.num
      return {
        position: [r * Math.sin(angle), relY, r * Math.cos(angle)],
        rotation: [0, angle, 0.5 * Math.PI],
      }
    },
    [show3D, componentBounds, radius, selectedModel]
  )

  const getMovementForCursorDelta = React.useCallback<MovementCalculator>(
    ([x, y]) => [y, -x],
    []
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => [Math.min(rect.width.num, radius.num * 2.0), rect.height.num],
    [radius]
  )

  //----------------------------------------------------------------------------

  return (
    <>
      <DefaultCamera
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getMovementForCursorDelta={getMovementForCursorDelta}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {/* Pipe shell ------------------------------------------------------- */}
      <PipeSurface3D model={selectedModel} />
    </>
  )
}
